/**
 * CausticLight — Animated dappled light texture projected onto the ocean floor.
 *
 * Instead of drei's <Caustics> (which computes refractive volumes from child geometry),
 * this uses a procedural caustic pattern generated in a fragment shader, scrolled
 * over time via useFrame. Nearly zero performance cost.
 *
 * Usage: Place as a sibling to OceanFloor inside the Canvas. It renders a transparent
 * plane slightly above the floor that blends the caustic pattern additively.
 */
import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const causticVertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const causticFragmentShader = /* glsl */ `
  uniform float uTime;
  uniform vec3 uColor;
  uniform float uIntensity;
  uniform float uScale;
  varying vec2 vUv;

  // Procedural caustic pattern using layered sine waves
  float caustic(vec2 uv) {
    float t = uTime * 0.15;
    vec2 p = uv * uScale;

    float c = 0.0;
    // Layer 1
    c += sin(p.x * 3.7 + t * 1.1 + sin(p.y * 4.3 + t * 0.7)) * 0.25;
    // Layer 2
    c += sin(p.y * 4.1 - t * 0.9 + sin(p.x * 3.3 - t * 1.3)) * 0.25;
    // Layer 3 (rotated)
    vec2 r = vec2(p.x * 0.866 - p.y * 0.5, p.x * 0.5 + p.y * 0.866);
    c += sin(r.x * 5.1 + t * 0.6 + sin(r.y * 3.9 + t * 1.0)) * 0.2;
    // Layer 4 (finer detail)
    c += sin(p.x * 7.3 + p.y * 5.7 + t * 0.4) * 0.15;

    // Remap to 0..1 and apply contrast curve
    c = c * 0.5 + 0.5;
    c = smoothstep(0.3, 0.8, c);
    return c;
  }

  void main() {
    float c = caustic(vUv);

    // Fade edges to avoid hard cutoff at plane boundary
    float edgeFade = 1.0;
    vec2 edge = smoothstep(0.0, 0.05, vUv) * smoothstep(0.0, 0.05, 1.0 - vUv);
    edgeFade = edge.x * edge.y;

    float alpha = c * uIntensity * edgeFade;
    gl_FragColor = vec4(uColor, alpha);
  }
`;

interface CausticLightProps {
  /** Size of the caustic plane (should match or exceed OceanFloor size) */
  size?: number;
  /** Caustic pattern color */
  color?: string;
  /** Brightness of the caustic pattern (0-1) */
  intensity?: number;
  /** Scale of the caustic pattern (higher = smaller/finer patterns) */
  scale?: number;
}

export function CausticLight({
  size = 15,
  color = "#B0C4DE",
  intensity = 0.2,
  scale = 4.0,
}: CausticLightProps) {
  const materialRef = useRef<THREE.ShaderMaterial>(null!);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(color) },
      uIntensity: { value: intensity },
      uScale: { value: scale },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useFrame((_, delta) => {
    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value += delta;
    }
  });

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
      <planeGeometry args={[size, size, 1, 1]} />
      <shaderMaterial
        ref={materialRef}
        vertexShader={causticVertexShader}
        fragmentShader={causticFragmentShader}
        uniforms={uniforms}
        transparent
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        side={THREE.FrontSide}
      />
    </mesh>
  );
}
